import {logError, memoize} from 'util/common'

import Cookies from 'js-cookie'
import MD5 from 'md5.js'
import {STAFF_EXPERIMENTS_OVERRIDE_COOKIE_NAME} from 'app/constants'
import analytics from 'analytics'
import flag from './flag'
import {getMobilizeAnonymousId} from 'util/anonymous-id'
import {getQueryParams} from 'util/url'

export const ExperimentSubject = Object.freeze({
  USER: 'User',
  EVENT: 'Event',
  ORGANIZATION: 'Organization',
})

// Subset of what's defined in the registry. We don't pass down nonessentials.

// Subset of what's defined in the registry. We don't pass down nonessentials.

const experiments = window.__MLZ_EXPERIMENTS__

// All experiments are gated with auto-generated Waffle flags. Here,
// check to make sure that the flag for the given experiment is on.
function isTurnedOn(experiment) {
  return flag.isActive(`enable_experiment_${experiment.name}`)
}

// The method to be called in JS to get an experiment treatment for a user. If no
// user is passed in, we rely on the visitor's anonymous id for the allocation.
// Memoized so that we avoid duplicating analytics events + other work.
export const maybeGetAllocationForMaybeUser = memoize(
  _maybeGetAllocationForMaybeUser,
  (experimentName, user) =>
    `${experimentName}|${_getStringIdForUserAllocation(user)}`
)

export function isUserInExperimentVariant(experimentName, user, variantName) {
  const alloc = maybeGetAllocationForMaybeUser(experimentName, user)
  return !!alloc && alloc.variant.name === variantName
}

// The method to be called in JS to get an experiment treatment for an event.
// Memoized so that we avoid duplicating underlying work.
export const maybeGetAllocationForEvent = memoize(
  _maybeGetAllocationForEvent,
  (experimentName, event) => `${experimentName}|${event.id}`
)

export function isEventInExperimentVariant(experimentName, event, variantName) {
  const alloc = maybeGetAllocationForEvent(experimentName, event)
  return !!alloc && alloc.variant.name === variantName
}

function _getStringIdForUserAllocation(user) {
  return user ? user.id.toString() : getMobilizeAnonymousId()
}

function _maybeGetAllocationForEvent(experimentName, event) {
  // Unlike _maybeGetAllocationForMaybeUser, we don't send tracking events from this function.
  // Since we can't reasonably allocate for events based on anonymous ID, we might as well
  // perform these allocations from the backend only (at least for the moment).
  const experiment = experiments[experimentName]
  if (!experiment) {
    logError(
      `Tried to get allocation for nonexistent experiment "${experimentName}"`
    )
    return null
  }

  if (experiment.subject !== ExperimentSubject.EVENT) {
    logError(
      `experiment ${experiment.name} was allocated to an event, but its subject is ${experiment.subject}`
    )
    return null
  }

  const maybeOverride = _maybeGetOverride(experiment)
  const allocation = _getAllocation(
    experiment,
    event.id.toString(),
    maybeOverride
  )

  return allocation
}

// The method to be called in JS to get an experiment treatment for an
// organization. Memoized so that we avoid duplicating underlying work.
export const maybeGetAllocationForOrganization = memoize(
  _maybeGetAllocationForOrganization,
  (experimentName, organization) => `${experimentName}|${organization.id}`
)

export function isOrganizationInExperimentVariant(
  experimentName,
  organization,
  variantName
) {
  const alloc = maybeGetAllocationForOrganization(experimentName, organization)
  return !!alloc && alloc.variant.name === variantName
}

function _maybeGetAllocationForOrganization(experimentName, organization) {
  // Unlike _maybeGetAllocationForMaybeUser, we don't send tracking events from this function.
  // Since we can't reasonably allocate for organizations based on anonymous ID, we might as well
  // perform these allocations from the backend only (at least for the moment).
  const experiment = experiments[experimentName]
  if (!experiment) {
    logError(
      `Tried to get allocation for nonexistent experiment "${experimentName}"`
    )
    return null
  }

  if (experiment.subject !== ExperimentSubject.ORGANIZATION) {
    logError(
      `experiment ${experiment.name} was allocated to an organization, but its subject is ${experiment.subject}`
    )
    return null
  }

  const maybeOverride = _maybeGetOverride(experiment)
  const allocation = _getAllocation(
    experiment,
    organization.id.toString(),
    maybeOverride
  )

  return allocation
}

function _maybeGetOverride(experiment) {
  // An override can either come from a query param or the staff experiments override cookie.
  let overrideVariantName

  const experimentName = experiment.name
  const queryParams = getQueryParams(window.location.search)
  const devExperimentsCookie = Cookies.get(
    STAFF_EXPERIMENTS_OVERRIDE_COOKIE_NAME
  )

  if (experimentName in queryParams) {
    // Detect a query param override, if present.
    overrideVariantName = queryParams[experimentName]
  } else if (devExperimentsCookie) {
    // Detect a staff debug override, if present.
    const cookieAsObject = JSON.parse(devExperimentsCookie)

    if (cookieAsObject.hasOwnProperty(experimentName)) {
      overrideVariantName = cookieAsObject[experimentName]
    }
  } else {
    // No overrides present.
    return null
  }

  // NB: If experiment has no such variant, returns null.
  return experiment.variants.find((v) => v.name === overrideVariantName)
}

function _maybeGetAllocationForMaybeUser(experimentName, user) {
  const experiment = experiments[experimentName]
  if (!experiment) {
    logError(
      `Tried to get allocation for nonexistent experiment "${experimentName}"`
    )
    return null
  }

  if (experiment.subject !== ExperimentSubject.USER) {
    logError(
      `experiment ${experiment.name} was allocated to a user, but its subject is ${experiment.subject}`
    )
    return null
  }

  const maybeOverride = _maybeGetOverride(experiment)
  const id = _getStringIdForUserAllocation(user)
  const allocation = _getAllocation(experiment, id, maybeOverride)

  // Only send a tracking event if an actual allocation happened; this works fine for control
  // because we explicitly declare it as a variant. (We'd have to revisit this if we want to
  // explicitly track "unallocated", but it should currently be inferrable from the absence of
  // any allocation events.)
  if (!allocation) {
    return null
  }

  analytics.trackExperimentAllocationByUser(experiment, allocation)

  const experimentTrait = {
    [`experiment_${experiment.name}`]: `${allocation.variant.name}`,
  }

  // Attach experiment allocation to user or anonymous user as a property.
  analytics.identifyAnonymous(experimentTrait)

  return allocation
}

// Helper that returns an allocation independent of underlying
// model given an integer ID, not to be called directly.
function _getAllocation(experiment, id, override) {
  if (!isTurnedOn(experiment)) {
    return null
  }

  let allocation = null
  let bucket = _computeHashForExperimentAndId(experiment.name, id)

  for (const variant of experiment.variants) {
    if (variant.bucket.start <= bucket && bucket < variant.bucket.end) {
      allocation = {variant, overridden: false, bucket}
      break
    }
  }

  if (override) {
    allocation = {variant: override, overridden: true, bucket: null}
  }

  return allocation
}

// Export for testing purposes only. Not be called directly.
export function _computeHashForExperimentAndId(experiment_name, id) {
  const hash = new MD5()
    .update(`${experiment_name}${id}`)
    .digest('hex')
    .slice(0, 10)
  return parseInt(hash, 16) % 100
}
